import React, { useState, useRef, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { Icon, IconColor, IconTypes } from '@app/ui'
import { Sport } from '../types'
import { useTranslation } from 'react-i18next'

interface SportsGridProps {
  sportsOptions: Sport[]
  selectedSports: string[]
  onSportToggle: (sportId: string) => void
  loading: boolean
  error: any
}

const ITEMS_PER_PAGE_MOBILE = 8
const ITEMS_PER_PAGE_DESKTOP = 12

export const SportsGrid: React.FC<SportsGridProps> = ({
  sportsOptions,
  selectedSports,
  onSportToggle,
  loading,
  error
}) => {
  const { t } = useTranslation()
  const [ currentPage, setCurrentPage ] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const dragStartX = useRef<number>(0)
  const isDragging = useRef<boolean>(false)
  const hasMoved = useRef<boolean>(false)

  const isMobile = window?.innerWidth < 500
  const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP

  const totalPages = Math.ceil(sportsOptions.length / itemsPerPage)

  const startIndex = currentPage * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentSports = sportsOptions.slice(startIndex, endIndex)

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    isDragging.current = true
    hasMoved.current = false
    dragStartX.current = 'touches' in e ? e.touches[0].clientX : e.clientX
  }

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging.current) return

    const currentX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const diff = Math.abs(dragStartX.current - currentX)

    if (diff > 5) {
      hasMoved.current = true
    }
  }

  const handleDragEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging.current) return

    const endX = 'changedTouches' in e ? e.changedTouches[0].clientX : (e as React.MouseEvent).clientX
    const diff = dragStartX.current - endX
    const threshold = 50

    if (hasMoved.current && Math.abs(diff) > threshold) {
      if (diff > 0) {
        handleNextPage()
      } else {
        handlePrevPage()
      }
    }

    isDragging.current = false
    hasMoved.current = false
  }

  const handleSportClick = (sportId: string) => {
    if (!hasMoved.current) {
      onSportToggle(sportId)
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const preventClick = (e: MouseEvent) => {
      if (hasMoved.current) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    container.addEventListener('click', preventClick, true)
    return () => container.removeEventListener('click', preventClick, true)
  }, [])

  if (loading) {
    return (
      <Container>
        {Array.from({ length: 4 }).map((_, index) => (
          <SkeletonSportButton key={`skeleton-${index}`}>
            <SkeletonIcon />
            <SkeletonText />
          </SkeletonSportButton>
        ))}
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{t('feed.errorLoadingSports')}</ErrorMessage>
      </Container>
    )
  }

  return (
    <>
      <Container
        ref={containerRef}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
      >
        {currentSports.map(sport => (
          <SportButton
            key={sport.id}
            selected={selectedSports.includes(sport.id)}
            onClick={() => handleSportClick(sport.id)}
            onMouseDown={(e) => {
              handleDragStart(e)
            }}
            onTouchStart={(e) => {
              handleDragStart(e)
            }}
          >
            <SportIcon>
              <Icon
                type={sport.icon}
                color={selectedSports.includes(sport.id) ? IconColor.violete600 : IconColor.zinc500}
                height={24}
                width={24}
              />
            </SportIcon>
            <SportName selected={selectedSports.includes(sport.id)}>
              {sport.name}
            </SportName>
          </SportButton>
        ))}
      </Container>

      {totalPages > 1 && (
        <NavigationWrapper>
          <NavButton onClick={handlePrevPage} disabled={currentPage === 0} isPrevious>
            <Icon type={IconTypes.angleLeft} color={IconColor.viloet400} height={20} width={20} />
          </NavButton>

          <PaginationDots>
            {Array.from({ length: totalPages }).map((_, index) => (
              <Dot key={index} active={index === currentPage} onClick={() => setCurrentPage(index)} />
            ))}
          </PaginationDots>

          <NavButton onClick={handleNextPage} disabled={currentPage === totalPages - 1}>
            <Icon type={IconTypes.angleRight} color={IconColor.viloet400} height={20} width={20} />
          </NavButton>
        </NavigationWrapper>
      )}
    </>
  )
}

const NavigationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 16px;
`

const NavButton = styled.button<{ disabled?: boolean, isPrevious?: boolean }>`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${props => props.disabled ? 0.3 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  transition: all 0.2s ease;
  padding: 0;
  ${props => props.isPrevious ? 'margin-right: auto;' : 'margin-left: auto;'}

  &:hover {
    opacity: ${props => props.disabled ? 0.3 : 0.7};
  }
`

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 4px;
  flex: 1;
  cursor: grab;
  user-select: none;
  touch-action: pan-y;
  position: relative;

  &:active {
    cursor: grabbing;
  }

  @media (min-width: 500px) {
    grid-template-columns: repeat(6, 1fr);
  }

  * {
    pointer-events: auto;
  }
`

const PaginationDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
`

const Dot = styled.button<{ active: boolean }>`
  width: ${props => props.active ? '24px' : '6px'};
  height: 6px;
  border-radius: 3px;
  background: ${props => props.active ? 'var(--bg-quinary)' : 'var(--bg-primary)'};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`

const SportButton = styled.button<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: ${props => props.selected ? '2px solid var(--bg-nonary)' : 'none'};
  background: var(--bg-primary);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 80px;
  width: 100%;
  aspect-ratio: 1;
`

const SportIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const SportName = styled.span<{ selected: boolean }>`
  font-size: 11px;
  font-weight: 500;
  color: var(--text-quaternary);
  text-align: center;
  line-height: 1.2;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 4px;
`


const ErrorMessage = styled.div`
  color: #EF4444;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`

const SkeletonSportButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 8px;
  border: 2px solid #E5E7EB;
  background: white;
  border-radius: 12px;
  min-height: 80px;
`

const SkeletonIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
`

const SkeletonText = styled.div`
  width: 40px;
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
`